// Goods issue (MIGO 261) of a spare part to THIS order — the physical counterpart of the
// component reservation. Material + storage location come from the order's components when
// present; free entry stays possible (unplanned consumption is the normal PM case).
import React, { useState, useRef } from 'react';
import { PackageMinus } from 'lucide-react';
import { useTheme } from '../../../../core/ui/ThemeContext';
import { useLanguage } from '../../../../core/i18n/LanguageContext';
import { Card, Button } from '../ui';
import { postGoodsIssue } from '../../api';

const input = (isDarkMode) =>
  `w-full rounded-xl px-3 py-2 ${isDarkMode ? 'bg-white/10 text-white placeholder-white/40' : 'bg-slate-100 text-slate-800 placeholder-slate-400'}`;

export default function GoodsIssuePanel({ user, order, role, onPosted }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [openForm, setOpenForm] = useState(false);
  const [materialId, setMaterialId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [storageLocation, setStorageLocation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [posted, setPosted] = useState(null);
  const idemKey = useRef(crypto.randomUUID());

  const sub = isDarkMode ? 'text-white/50' : 'text-slate-500';

  const prefill = (c) => {
    setOpenForm(true);
    setMaterialId(c.materialId || '');
    setQuantity(String(c.requiredQty || 1));
    setStorageLocation(c.storageLocation || '');
  };

  return (
    <Card className="mb-3">
      <button type="button" onClick={() => setOpenForm((v) => !v)}
        className={`w-full flex items-center gap-2 font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
        <PackageMinus size={18} /> {t('pm.gi.title')}
      </button>
      {openForm && (
        <div className="mt-3 space-y-2">
          <p className={`text-xs ${sub}`}>{t('pm.gi.hint')}</p>
          {(order.components || []).length > 0 && (
            <div className={`text-sm ${sub}`}>
              {t('pm.gi.fromComponents')}{' '}
              {order.components.map((c) => (
                <button key={c.itemNo} type="button" className="underline mr-2" onClick={() => prefill(c)}>
                  {c.materialId}
                </button>
              ))}
            </div>
          )}
          <input className={input(isDarkMode)} placeholder={t('pm.gi.material')} value={materialId}
            onChange={(e) => setMaterialId(e.target.value)} />
          <div className="flex gap-2">
            <input className={input(isDarkMode)} type="number" min="0.001" step="any" value={quantity}
              onChange={(e) => setQuantity(e.target.value)} />
            <input className={input(isDarkMode)} placeholder={t('pm.gi.storageLocation')} value={storageLocation}
              onChange={(e) => setStorageLocation(e.target.value)} />
          </div>
          <Button variant="confirm" className="!min-h-[48px]" disabled={busy || !materialId || !storageLocation}
            onClick={async () => {
              setBusy(true); setError(null); setPosted(null);
              try {
                const res = await postGoodsIssue(user, order.orderId, {
                  materialId, quantity: Number(quantity), storageLocation,
                }, idemKey.current, role);
                if (res && res.ok === false) setError(res.error || t('pm.gi.failed'));
                else {
                  idemKey.current = crypto.randomUUID();
                  setPosted(res);
                  if (onPosted) onPosted();
                }
              } catch (e) { setError(e.message); } finally { setBusy(false); }
            }}>
            {t('pm.gi.post')}
          </Button>
          {error && <p className={isDarkMode ? 'text-rose-300 text-sm' : 'text-rose-600 text-sm'}>{error}</p>}
          {posted && (
            <p className={isDarkMode ? 'text-emerald-300 text-sm' : 'text-emerald-700 text-sm'}>
              {t('pm.gi.posted', { doc: posted.materialDocId || '' })}
            </p>
          )}
        </div>
      )}
    </Card>
  );
}
